import { useEffect, useMemo, useState } from "react";
import { getSingleSelectionTarget } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import { storeKeyToEntityId, useEntity } from "../ecs/index.ts";
import { Button } from "./Button.tsx";
import {
  type BevelStepSnapshot,
  buildBevelDebugReport,
  collectBevelDebugSnapshot,
  type GeometrySummary,
} from "./bevelDebugExport.ts";
import { CopyButton } from "./CopyButton.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import {
  COLOR_DEBUG_HIGHLIGHT_BG,
  COLOR_DEBUG_OVERLAY_BG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_MUTED,
  FONT_SIZE_TINY,
  RADIUS_XS,
  Z_DEBUG,
} from "./tokens.ts";

const CANONICAL_FIXTURE_PATH =
  "apps/web/public/debug-scenes/bevel-faceted-repro.pr";

function formatReasonLabel(reason: string): string {
  switch (reason) {
    case "angleLimit":
      return "angle";
    case "vertexGroup":
      return "vertex group";
    case "nonManifold":
      return "non-manifold";
    default:
      return reason;
  }
}

function summaryLine(summary: GeometrySummary): string {
  const invalid =
    summary.invalidFaces.length > 0
      ? ` invalid=${summary.invalidFaces.length}`
      : "";
  return `v ${summary.vertexCount}  e ${summary.edgeCount}  f ${summary.faceCount}${invalid}`;
}

function deltaLine(before: GeometrySummary, after: GeometrySummary): string {
  const dv = after.vertexCount - before.vertexCount;
  const de = after.edgeCount - before.edgeCount;
  const df = after.faceCount - before.faceCount;
  return `Δv ${dv >= 0 ? "+" : ""}${dv}  Δe ${de >= 0 ? "+" : ""}${de}  Δf ${df >= 0 ? "+" : ""}${df}`;
}

function StepPill({
  step,
  active,
  onClick,
}: {
  step: BevelStepSnapshot;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      data-no-drag
      onClick={onClick}
      variant="ghost"
      size="xs"
      style={{
        border: `1px solid ${active ? "rgba(0,255,0,0.35)" : "rgba(0,255,0,0.12)"}`,
        background: active
          ? COLOR_DEBUG_HIGHLIGHT_BG
          : "rgba(255,255,255,0.02)",
        color: active ? "#0f0" : COLOR_TEXT_MUTED,
        borderRadius: RADIUS_XS,
        padding: "4px 6px",
        fontFamily: "monospace",
        fontSize: FONT_SIZE_TINY,
        textAlign: "left",
      }}
    >
      {`Step ${step.stepNumber}`}
    </Button>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
      <span style={{ width: 88, color: COLOR_TEXT_DIM, flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ color: "#d7ffd7", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

export function BevelDebugPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.bevelDebug);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const sceneObjects = useStore((s) => s.sceneObjects);
  const modifierOverrides = useStore((s) => s.modifierOverrides);
  const sel = usePanelSelection("debug-bevel", () => togglePanel("bevelDebug"));

  const objectId =
    selectedTarget?.kind === "sceneObject" ? selectedTarget.id : null;
  const entity = useEntity(objectId ? storeKeyToEntityId(objectId) : null);
  const object = objectId ? (sceneObjects.get(objectId) ?? null) : null;
  const overrides = objectId ? (modifierOverrides.get(objectId) ?? []) : [];
  const baseModifiers = entity?.components.modifierStack?.baseModifiers ?? [];

  const snapshot = useMemo(
    () =>
      object
        ? collectBevelDebugSnapshot(
            object,
            baseModifiers,
            overrides,
            sceneObjects,
            modifierOverrides,
          )
        : null,
    [object, baseModifiers, overrides, sceneObjects, modifierOverrides],
  );

  const report = useMemo(
    () =>
      object
        ? buildBevelDebugReport(
            object,
            baseModifiers,
            overrides,
            sceneObjects,
            modifierOverrides,
          )
        : null,
    [object, baseModifiers, overrides, sceneObjects, modifierOverrides],
  );

  const [activeStepNumber, setActiveStepNumber] = useState<number>(1);

  useEffect(() => {
    const first = snapshot?.steps[0]?.stepNumber ?? 1;
    const exists = snapshot?.steps.some(
      (step) => step.stepNumber === activeStepNumber,
    );
    if (!exists) setActiveStepNumber(first);
  }, [snapshot, activeStepNumber]);

  if (!debugEnabled || !panelOpen) return null;

  const activeStep =
    snapshot?.steps.find((step) => step.stepNumber === activeStepNumber) ??
    snapshot?.steps[0] ??
    null;
  const summaryText = snapshot
    ? JSON.stringify(snapshot, null, 2)
    : "No bevel snapshot available.";

  return (
    <DraggablePanel
      id="debug-bevel"
      defaultPosition={{ top: 8, right: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      resizable
      defaultSize={{ width: 420, height: 520 }}
      minSize={{ width: 320, height: 260 }}
      maxSize={{ width: 720, height: 820 }}
      selectionColor="#ff0"
      tint="rgba(0,255,0,0.06)"
      style={{ background: COLOR_DEBUG_OVERLAY_BG }}
      {...sel}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            fontFamily: "monospace",
            fontSize: FONT_SIZE_TINY,
          }}
        >
          <span style={{ color: "#0f0", fontWeight: "bold" }}>BEVEL DEBUG</span>
          {snapshot && (
            <span style={{ color: COLOR_TEXT_DIM }}>
              {snapshot.objectName} · {snapshot.bevelModifierCount} bevel
              {snapshot.bevelModifierCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      }
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 0,
          height: "100%",
          background: COLOR_DEBUG_OVERLAY_BG,
          color: "#d7ffd7",
          fontFamily: "monospace",
          fontSize: FONT_SIZE_TINY,
          padding: "8px 10px 10px",
          borderRadius: "0 0 6px 6px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <CopyButton
            getText={() => report ?? "No bevel report available."}
            label="Copy Report"
            copiedLabel="Report Copied"
          />
          <CopyButton
            getText={() => summaryText}
            label="Copy Snapshot"
            copiedLabel="Snapshot Copied"
          />
          <span style={{ color: COLOR_TEXT_DIM }}>
            Fixture: {CANONICAL_FIXTURE_PATH}
          </span>
        </div>

        {!object && (
          <div style={{ color: COLOR_TEXT_FAINT, lineHeight: 1.6 }}>
            Select a single mesh object with a Bevel modifier to inspect it
            here.
          </div>
        )}

        {object && !snapshot && (
          <div style={{ color: COLOR_TEXT_FAINT, lineHeight: 1.6 }}>
            Selected object has no Bevel modifier in its current stack.
          </div>
        )}

        {object && snapshot && !activeStep && (
          <div style={{ color: COLOR_TEXT_FAINT, lineHeight: 1.6 }}>
            Selected object has Bevel modifiers, but none are currently enabled.
          </div>
        )}

        {object && snapshot && activeStep && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                padding: "6px 8px",
                border: "1px solid rgba(0,255,0,0.12)",
                borderRadius: RADIUS_XS,
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <KeyValue
                label="Object"
                value={`${snapshot.objectName} (${snapshot.objectId})`}
              />
              <KeyValue
                label="Modifiers"
                value={`${snapshot.modifierCount} total · ${snapshot.bevelModifierCount} bevel`}
              />
              <KeyValue label="Active Step" value={activeStep.modifierLabel} />
              <KeyValue label="Settings" value={activeStep.settingsLine} />
            </div>

            {snapshot.steps.length > 1 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {snapshot.steps.map((step) => (
                  <StepPill
                    key={step.stepNumber}
                    step={step}
                    active={step.stepNumber === activeStep.stepNumber}
                    onClick={() => setActiveStepNumber(step.stepNumber)}
                  />
                ))}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 6,
                padding: "6px 8px",
                border: "1px solid rgba(0,255,0,0.12)",
                borderRadius: RADIUS_XS,
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <div style={{ color: "#0f0" }}>Topology</div>
              <KeyValue
                label="Input"
                value={summaryLine(activeStep.inputSummary)}
              />
              {activeStep.stage1Summary && (
                <KeyValue
                  label="Stage1"
                  value={summaryLine(activeStep.stage1Summary)}
                />
              )}
              <KeyValue
                label="Output"
                value={summaryLine(activeStep.outputSummary)}
              />
              <KeyValue
                label="Delta"
                value={deltaLine(
                  activeStep.inputSummary,
                  activeStep.outputSummary,
                )}
              />
            </div>

            {activeStep.selectionSummary && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 6,
                  padding: "6px 8px",
                  border: "1px solid rgba(0,255,0,0.12)",
                  borderRadius: RADIUS_XS,
                  background: "rgba(255,255,255,0.02)",
                }}
              >
                <div style={{ color: "#0f0" }}>Edge Selection</div>
                <KeyValue
                  label="Selected"
                  value={String(activeStep.selectionSummary.selectedEdgeCount)}
                />
                <KeyValue
                  label="Profiles"
                  value={`convex ${activeStep.selectionSummary.convexSelected} · concave ${activeStep.selectionSummary.concaveSelected} · unknown ${activeStep.selectionSummary.unknownSelected}`}
                />
                <KeyValue
                  label="Rejected"
                  value={String(
                    activeStep.selectionSummary.rejectedInteriorEdges,
                  )}
                />
                <KeyValue
                  label="Touched Faces"
                  value={String(activeStep.selectionSummary.touchedFaceCount)}
                />
                <KeyValue
                  label="Reasons"
                  value={Object.entries(
                    activeStep.selectionSummary.reasonCounts,
                  )
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(
                      ([reason, count]) =>
                        `${formatReasonLabel(reason)} ${count}`,
                    )
                    .join(" · ")}
                />
              </div>
            )}

            {activeStep.outputDiagnostics && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 6,
                  padding: "6px 8px",
                  border: "1px solid rgba(0,255,0,0.12)",
                  borderRadius: RADIUS_XS,
                  background: "rgba(255,255,255,0.02)",
                }}
              >
                <div style={{ color: "#0f0" }}>Output Quality</div>
                <KeyValue
                  label="Twisted"
                  value={String(activeStep.outputDiagnostics.twistedQuadCount)}
                />
                <KeyValue
                  label="Non-Planar"
                  value={String(
                    activeStep.outputDiagnostics.nonPlanarQuadCount,
                  )}
                />
                <KeyValue
                  label="Degenerate"
                  value={String(
                    activeStep.outputDiagnostics.degenerateFaceCount,
                  )}
                />
                <KeyValue
                  label="Face Flips"
                  value={String(
                    activeStep.outputDiagnostics.flippedAdjacentPairCount,
                  )}
                />
                {activeStep.outputDiagnostics.suspectFaces.length > 0 && (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <div style={{ color: COLOR_TEXT_DIM }}>Worst Faces</div>
                    {activeStep.outputDiagnostics.suspectFaces
                      .slice(0, 6)
                      .map((face) => (
                        <div
                          key={`${face.issue}-${face.faceIndex}`}
                          style={{ color: "#ffcf9f" }}
                        >
                          {`f${face.faceIndex} ${face.issue} · fold ${face.foldAngleDegrees == null ? "n/a" : `${face.foldAngleDegrees.toFixed(1)}°`} · area ${face.area.toFixed(6)}`}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                minHeight: 0,
                flex: 1,
              }}
            >
              <div style={{ color: COLOR_TEXT_DIM }}>Structured Snapshot</div>
              <pre
                style={{
                  margin: 0,
                  flex: 1,
                  minHeight: 120,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(0,255,0,0.12)",
                  borderRadius: RADIUS_XS,
                  padding: "8px",
                  color: "#bfffbf",
                }}
              >
                {summaryText}
              </pre>
            </div>
          </>
        )}
      </div>
    </DraggablePanel>
  );
}
